import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { SSEProvider } from "./hooks/useSSE";
import { PendingProvider } from "./hooks/usePending";
import { TodayProvider } from "./hooks/useToday";
import { NavProvider } from "./hooks/useNav";
import Sidebar from "./crm/Sidebar";
import MobileNav from "./crm/MobileNav";
import FeedbackButton from "./feedback/FeedbackButton";
import { repairPushSubscription } from "./pushRepair";
import Placeholder from "./crm/Placeholder";
import LoginPage from "./pages/LoginPage";
import LeadsPage from "./pages/LeadsPage";
import SettingsPage from "./crm/SettingsPage";
import BoardPage from "./crm/BoardPage";
import TodayPage from "./crm/TodayPage";
import FollowUpsPage from "./crm/FollowUpsPage";
import RuleBuilderPage from "./crm/RuleBuilderPage";
import AutomationsPage from "./crm/AutomationsPage";
import AutomationDetailPage from "./crm/AutomationDetailPage";
import ApprovalsPage from "./crm/ApprovalsPage";
import LeadsListPage from "./crm/LeadsListPage";
import ContactDetailPage from "./crm/ContactDetailPage";
import ReportsPage from "./crm/ReportsPage";
import BookingAdminPage from "./crm/BookingAdminPage";
import AvailabilityPage from "./crm/AvailabilityPage";
import ScoringPage from "./crm/ScoringPage";
import LandingPagesPage from "./crm/landing/LandingPagesPage";
import AnalyticsPage from "./crm/analytics/AnalyticsPage";
import CalendarPage from "./crm/CalendarPage";
import CallsPage from "./crm/calls/CallsPage";
import ListsIndexPage from "./crm/lists/ListsIndexPage";
import ListPage from "./crm/lists/ListPage";
import CheckPeoplePage from "./crm/lists/CheckPeoplePage";
import RoomsPage from "./crm/rooms/RoomsPage";
import LibraryPage from "./crm/rooms/LibraryPage";
import BookPage from "./booking/BookPage";
import BookedPage from "./booking/BookedPage";
import ManagePage from "./booking/ManagePage";
import ReadPage from "./booking/ReadPage";
import "./booking/booking.css";
import "./booking/booking-desktop.css";

// Authenticated app frame: sidebar + scrollable main. Individual views
// render their own topbar. On a phone the sidebar is hidden and MobileNav puts
// the same navigation in a bottom tab bar (plus the "+" that creates a lead).
function Shell({ children }) {
  const { isLoggedIn } = useAuth();
  // Once per open: a phone whose alert subscription went stale fixes itself.
  useEffect(() => {
    if (isLoggedIn) repairPushSubscription();
  }, [isLoggedIn]);
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return (
    <SSEProvider>
      <PendingProvider>
        <TodayProvider>
        <NavProvider>
        <div className="crm-app">
          <Sidebar />
          <main className="crm-main">{children}</main>
          <MobileNav />
          <FeedbackButton />
        </div>
        </NavProvider>
        </TodayProvider>
      </PendingProvider>
    </SSEProvider>
  );
}

export default function App() {
  const routes = [
    ["/today", <TodayPage />],
    ["/", <BoardPage />],
    ["/approvals", <ApprovalsPage />],
    ["/leads", <LeadsListPage />],
    ["/lists", <ListsIndexPage />],
    ["/lists/check", <CheckPeoplePage />],
    ["/lists/:id", <ListPage />],
    // Prospect rooms (src/rooms): the list of rooms, and the decks they pick from.
    ["/sales/rooms", <RoomsPage />],
    ["/sales/library", <LibraryPage />],
    ["/follow-ups", <FollowUpsPage />],
    ["/follow-ups/rules/:id", <RuleBuilderPage />],
    // Automations sit beside Follow-ups: the two are the same idea, one showing
    // you a row and the other doing something about it.
    ["/automations", <AutomationsPage />],
    // Each area lists only its own automations (navModel.AREA_OF_TYPE). The
    // unfiltered list above stays reachable for old links; the menu no longer
    // points at it.
    ["/inbound/automations", <AutomationsPage area="inbound" />],
    ["/meetings/automations", <AutomationsPage area="meetings" />],
    ["/meetings/availability", <AvailabilityPage />],
    ["/inbound/scoring", <ScoringPage />],
    ["/inbound/landing-pages", <LandingPagesPage />],
    // Who is on the site right now, and how a date range went (src/analytics).
    ["/inbound/analytics", <AnalyticsPage />],
    ["/meetings/calendar", <CalendarPage />],
    // Every Fireflies call with its notes and transcript (src/calls); a lead's
    // record links to one call by its Fireflies id.
    ["/meetings/calls", <CallsPage />],
    ["/meetings/calls/:id", <CallsPage />],
    ["/automations/:id", <AutomationDetailPage />],
    ["/booking-page", <BookingAdminPage />],
    ["/contacts", <Placeholder title="Contacts" />],
    ["/contacts/:id", <ContactDetailPage />],
    ["/reports", <ReportsPage />],
    ["/email-queue", <LeadsPage />],
    ["/settings", <SettingsPage />],
  ];
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Public, no login: the page a lead books a call with. */}
        <Route path="/book" element={<BookPage />} />
        <Route path="/book/booked/:token" element={<BookedPage />} />
        <Route path="/book/manage/:token" element={<ManagePage />} />
        <Route path="/book/read/:id" element={<ReadPage />} />
        {/* A meeting type's private link (friendly chat, …); /book itself is the intro call. */}
        <Route path="/book/:slug" element={<BookPage />} />
        {routes.map(([path, element]) => (
          <Route key={path} path={path} element={<Shell>{element}</Shell>} />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
